export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

// API 基础地址
// 开发环境：使用 vite proxy 代理到后端
// 生产环境：由 vite.config.ts 中的 PRODUCTION_API_URL 配置决定
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// 全局标志，防止重复显示登录过期弹窗
let isTokenExpiredDialogShowing = false;

// 令牌过期处理函数
function handleTokenExpired() {
  // 如果已经在显示弹窗，直接返回，避免重复弹窗
  if (isTokenExpiredDialogShowing) {
    return;
  }
  
  // 检查是否有用户令牌
  const userToken = localStorage.getItem('userToken');
  const adminToken = localStorage.getItem('adminToken');
  
  // 设置标志，表示正在处理令牌过期
  isTokenExpiredDialogShowing = true;
  
  if (userToken) {
    // 清除过期的用户令牌
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    
    // 触发自定义事件，让 AuthContext 监听并处理
    // 不要直接刷新页面，让 React 自然更新状态
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api:unauthorized', { 
        detail: { reason: 'token_expired' } 
      }));
    }
  } else if (adminToken) {
    // 清除过期的管理员令牌
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    
    // 如果在管理员页面，延迟跳转避免频繁跳转
    if (typeof window !== 'undefined') {
      const isAdminRoute = window.location.pathname.startsWith('/admin') && 
                          window.location.pathname !== '/admin/login';
      
      if (isAdminRoute) {
        // 保存当前路径，登录后可以重定向回来
        const currentPath = window.location.pathname + window.location.search;
        sessionStorage.setItem('adminRedirectPath', currentPath);
        
        // 延迟跳转，给用户一个缓冲时间
        console.log('管理员登录已过期，将在2秒后跳转到登录页面...');
        setTimeout(() => {
          window.location.href = '/admin/login?expired=true';
        }, 2000);
        return;
      }
    }
  }
  
  // 延迟重置标志，避免短时间内重复弹窗
  setTimeout(() => {
    isTokenExpiredDialogShowing = false;
  }, 1000);
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  // 记录请求体（隐藏敏感信息）
  let requestBodyLog: any = null;
  if (options.body) {
    try {
      const bodyObj = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
      // 创建安全日志对象（隐藏密码）
      requestBodyLog = { ...bodyObj };
      if (requestBodyLog.password) {
        requestBodyLog.password = '***';
      }
      if (requestBodyLog.verification_code) {
        requestBodyLog.verification_code = '***';
      }
    } catch {
      requestBodyLog = '[无法解析]';
    }
  }
  
  console.log('🌐 [API请求] 开始:', { 
    path, 
    method: options.method || 'GET',
    body: requestBodyLog 
  });
  
  // 智能选择token：根据当前路径和API路径判断使用哪个token
  const userToken = typeof window !== 'undefined' ? localStorage.getItem('userToken') : null;
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null;
  
  // 判断是否为管理后台相关请求
  const isAdminPath = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
  const isAdminAPI = path.startsWith('/admin/');
  
  // 智能选择token：
  // 1. 如果是管理后台页面或管理后台API，优先使用adminToken
  // 2. 否则优先使用userToken
  const token = (isAdminPath || isAdminAPI) 
    ? (adminToken || userToken)  // 管理后台优先用adminToken
    : (userToken || adminToken); // 用户端优先用userToken
  
  try {
    // 创建超时控制器（30秒超时）
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    
    const res = await fetch(`${API_BASE_URL}${path}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
      credentials: 'include',
      signal: controller.signal,
      ...options,
    });
    
    clearTimeout(timeoutId);
    
    console.log('🌐 [API响应] 状态码:', res.status, '路径:', path);
    
    if (!res.ok) {
      const text = await res.text();
      
      // 尝试解析错误响应
      let errorData;
      try {
        errorData = JSON.parse(text);
      } catch {
        errorData = { msg: text };
      }
      
      // 获取错误消息（支持error、msg或message字段）
      const errorMsg = errorData.error || errorData.msg || errorData.message || '';
      
      // 检查是否是预期的业务逻辑响应（不应作为错误记录）
      const isExpectedBusinessResponse = 
        (res.status === 404 && path === '/merchants/me' && errorMsg.includes('还没有创建商家信息')) || // 保留以兼容旧版本
        (res.status === 404 && path.startsWith('/distribution-channels') && errorMsg.includes('还没有创建商家信息')) ||
        (errorData.has_merchant === false) ||
        (res.status === 401 && (errorMsg.includes('令牌已过期') || errorMsg.includes('无效的令牌') || errorMsg.includes('未提供令牌')));
      
      // 只记录真正的错误，跳过预期的业务逻辑响应
      if (!isExpectedBusinessResponse) {
        console.error('🌐 [API错误] 响应内容:', text);
        console.error('🌐 [API错误] 错误消息:', errorMsg);
      } else {
        console.log('💡 [API业务响应] 状态码:', res.status, '路径:', path, '消息:', errorMsg);
      }
      
      // 特殊处理401未授权错误
      if (res.status === 401) {
        // 检查是否是登录相关的API（登录失败是正常业务逻辑，不应触发登录过期）
        const isAuthAPI = path.startsWith('/auth/login') || 
                         path.startsWith('/auth/register') || 
                         path.startsWith('/auth/reset-password');
        
        if (isAuthAPI) {
          // 登录、注册、重置密码等接口的401错误，直接抛出原始错误消息
          throw new Error(errorMsg || '认证失败');
        }
        
        // 检查是否是验证码错误（不应触发登录过期）
        if (errorMsg.includes('验证码错误') || errorMsg.includes('验证码已过期')) {
          throw new Error(errorMsg);
        }
        
        // 对于其他需要认证的API，401表示token过期或无效
        // 静默处理（不要显示红色错误），让用户自然地重新登录
        console.log('🔒 [Token过期] 检测到token过期，自动清理登录状态');
        handleTokenExpired();
        throw new Error('登录已过期，请重新登录');
      }
      
      // 对于其他错误状态码，返回解析后的错误消息
      throw new Error(errorMsg || `请求失败: ${res.status}`);
    }
    
    const json = (await res.json()) as ApiResponse<T> | T;
    console.log('✅ [API成功] 响应数据:', json);
    const hasData = !!(json as any).data || Array.isArray(json) || (typeof json === 'object' && json !== null && Object.keys(json).length > 0);
    console.log('📊 [API成功] json类型:', typeof json, 'hasData:', hasData);
    
    // 如果响应包含 code 和 data 字段（标准 API 响应格式），返回完整响应对象
    // 这样可以保留 code、data 等信息，前端可以根据 code 判断成功与否
    if (json && typeof json === 'object' && 'code' in json && 'data' in json) {
      // @ts-ignore
      const result = json as T;
      console.log('📤 [API成功] 返回结果（完整响应对象）:', result);
      return result;
    }
    
    // 支持直接数据或包一层 data 的两种风格（兼容旧代码）
    // @ts-ignore
    const result = (json && (json.data ?? json)) as T;
    console.log('📤 [API成功] 返回结果:', result);
    return result;
  } catch (error: any) {
    // 处理请求超时
    if (error.name === 'AbortError') {
      console.error('❌ [API超时] 请求超时:', path);
      throw new Error('请求超时，请检查网络连接后重试');
    }
    
    // 检查是否是预期的业务逻辑错误（不作为异常记录）
    const isExpectedBusinessError = 
      error.message?.includes('还没有创建商家信息') ||
      error.message?.includes('has_merchant');
    
    if (!isExpectedBusinessError) {
      console.error('❌ [API异常] 请求失败:', {
        path,
        method: options.method || 'GET',
        error: error.message,
        stack: error.stack
      });
    }
    throw error;
  }
}

export const api = {
  get: <T>(path: string, options?: { params?: Record<string, any> }) => {
    // Build query string from params if provided
    let url = path;
    if (options?.params) {
      const queryParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
      const queryString = queryParams.toString();
      if (queryString) {
        url = `${path}?${queryString}`;
      }
    }
    return request<T>(url);
  },
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export default api;

